import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import {
  useAgreeAiService,
  useGetAiServiceSetting,
} from '@/context/services/ai_service_setting/AiServiceSetting.service';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { FC, Suspense, useCallback, useState } from 'react';
import { InitialSetting } from './InitialSetting/InitialSetting';
import styles from './styles.module.scss';

export const InnerAiServiceSetting: FC = () => {
  const setting = useGetAiServiceSetting();
  const [isAgreed, setIsAgreed] = useState(setting.is_ai_service_agreed);
  const [isModelTrainingAllowed, setIsModelTrainingAllowed] = useState(
    setting.is_model_training_allowed
  );
  const { isPending, mutateAsync } = useAgreeAiService();
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { send } = useErrorTrackingSystem();

  const notifySuccess = useCallback(
    (message: string) => {
      notify.success({
        duration: 5,
        title: message,
      });
    },
    [notify]
  );

  const notifyError = useCallback(
    (message: string) => {
      notify.error({
        duration: 5,
        title: message,
      });
    },
    [notify]
  );

  const handleAgreement = async () => {
    try {
      const result = await mutateAsync(isModelTrainingAllowed);

      if (result.isSuccessful) {
        notifySuccess(t('ai_service_setting.agreement_success'));
        setIsAgreed(true);
        return;
      }

      notifyError(result.message);
    } catch (error) {
      send(error instanceof Error ? error : String(error));
      notifyError(t('ai_service_setting.agreement_error'));
    }
  };

  if (isAgreed) {
    // TODO: Implement the agreed setting later.
    return null;
  }

  return (
    <InitialSetting
      isAgreementPending={isPending}
      isModelTrainingAllowed={isModelTrainingAllowed}
      onAgreement={handleAgreement}
      onModelTrainingAllowedChange={setIsModelTrainingAllowed}
      services={setting.ai_service_settings}
    />
  );
};

export const AiServiceSetting: FC = () => (
  <Suspense fallback={<SuspenseLoading className={styles.loadingContainer} />}>
    <InnerAiServiceSetting />
  </Suspense>
);
